import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Users, ArrowLeft, Mail, Phone, User, Calendar, Archive, ClipboardList, Star, Info, Edit, ArchiveRestore, Trash2, FileText, AlertTriangle, Plus, ArchiveX } from 'lucide-react';
import Header from './Header';
import ClientForm from './ClientForm';
import BrandBadge from './BrandBadge';
import BlacklistAlert, { BlacklistBadge, BlacklistWarningCard } from './BlacklistAlert';
import BlacklistIncidentForm from './BlacklistIncidentForm';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, getOrderTypeDisplayText, getStatusColor } from '../lib/orderService';
import { 
  getClientIncidents, 
  archiveIncident, 
  checkClientBlacklistStatus,
  formatIncidentDate,
  getIncidentStatusColor 
} from '../lib/blacklistService';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user came from order detail
  const searchParams = new URLSearchParams(location.search);
  const fromOrder = searchParams.get('from') === 'order';
  const orderId = searchParams.get('orderId');
  
  const { currentUser, userRole } = useAuth();
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isClientInUse, setIsClientInUse] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchClientData();
    fetchBrands();
    fetchIncidents();
  }, [id]);

  // Reset pagination when orders change
  useEffect(() => {
    setCurrentPage(1);
  }, [orders.length]);

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const canManageBlacklist = () => {
    return userRole === 'admin';
  };

  // Helper function to get order type colors
  const getOrderTypeColor = (tipo) => {
    const typeColors = {
      'Reparación': 'bg-blue-50 text-blue-700',
      'Ensamble': 'bg-purple-50 text-purple-700',
      'Garantía': 'bg-orange-50 text-orange-700',
      'Fabricación': 'bg-gray-50 text-gray-700'
    };
    
    return typeColors[tipo] || 'bg-gray-50 text-gray-700';
  };

  // Helper function to get relative time
  const getRelativeTime = (fecha) => {
    const now = new Date();
    const orderDate = new Date(fecha);
    const diffTime = Math.abs(now - orderDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return '(hoy)';
    } else if (diffDays === 1) {
      return '(ayer)';
    } else if (diffDays <= 7) {
      return `(hace ${diffDays} días)`;
    } else {
      return '';
    }
  };

  // Helper function to get equipment summary
  const getEquipmentSummary = (equipos) => {
    if (!equipos || equipos.length === 0) {
      return 'Sin equipos';
    }

    return equipos.map((equipo, index) => {
      if (equipo.type === 'catalog' && equipo.catalogData) {
        // Catalog equipment
        const type = equipo.catalogData.tipo || 'Equipo';
        const brand = equipo.catalogData.marca || '';
        const model = equipo.catalogData.modelo || '';
        const partNumber = equipo.catalogData.numeroParte || '';
        const serial = equipo.series || '';
        
        return (
          <div key={index} className="text-xs text-gray-600 dark:text-gray-300">
            {type} <BrandBadge brand={getBrandByNameOrId(brand)} size="xs" className="mr-1" /> <span className="font-medium">{model}</span>
            {partNumber && ` (${partNumber})`}
            {serial && `, S/N: ${serial}`}
          </div>
        );
      } else {
        // Exempt equipment - try multiple possible field names
        const description = equipo.descripcion || equipo.description || equipo.nombre || 'Equipo sin descripción';
        const serial = equipo.series || '';
        
        return (
          <div key={index} className="text-xs text-gray-600 dark:text-gray-300">
            {description}
            {serial && ` - S/N: ${serial}`}
          </div>
        );
      }
    });
  };

  const handleEdit = () => {
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setSuccess('Cliente actualizado exitosamente');
    setShowForm(false);
    fetchClientData(); // Refresh client data
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleFormClose = () => {
    setShowForm(false);
  };

  const handleAddIncident = () => {
    setEditingIncident(null);
    setShowIncidentForm(true);
  };

  const handleEditIncident = (incident) => {
    setEditingIncident(incident);
    setShowIncidentForm(true);
  };

  const handleIncidentFormSuccess = (message) => {
    setSuccess(message);
    setShowIncidentForm(false);
    setEditingIncident(null);
    
    // Refresh data
    fetchClientData();
    fetchIncidents();
    
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleIncidentFormClose = () => {
    setShowIncidentForm(false);
    setEditingIncident(null);
  };

  const handleBlacklistWarningClick = (incidentId) => {
    // Navigate to blacklist incident detail with back navigation support
    const params = new URLSearchParams();
    params.set('from', 'client');
    params.set('clientId', client.id);
    navigate(`/clients/blacklist/${incidentId}?${params.toString()}`);
  };

  const handleArchiveIncident = async (incident) => {
    if (!window.confirm(`¿Está seguro de archivar el incidente "${incident.motivoIncidente}"?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await archiveIncident(incident.id, currentUser?.uid || 'system');
      setSuccess('Incidente archivado exitosamente');
      
      // Refresh data
      fetchClientData();
      fetchIncidents();
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving incident:', error);
      setError('Error al archivar el incidente');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const checkClientInOrders = async (clientId) => {
    try {
      // Check if client is assigned to any orders
      const ordersQuery = query(
        collection(db, 'orders'), 
        where('cliente', '==', clientId)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      return !ordersSnapshot.empty;
    } catch (error) {
      console.error('Error checking client in orders:', error);
      return true; // Assume client is in use if there's an error
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(`¿Está seguro de archivar a ${getClientDisplayName()}?`)) return;

    try {
      setActionLoading(true);
      await updateDoc(doc(db, 'clients', client.id), {
        isArchived: true,
        updatedAt: new Date()
      });
      setSuccess(`${getClientDisplayName()} ha sido archivado`);
      await fetchClientData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving client:', error);
      setError('Error al archivar el cliente');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnarchive = async () => {
    if (!window.confirm(`¿Está seguro de desarchivar a ${getClientDisplayName()}?`)) return;

    try {
      setActionLoading(true);
      await updateDoc(doc(db, 'clients', client.id), {
        isArchived: false,
        updatedAt: new Date()
      });
      setSuccess(`${getClientDisplayName()} ha sido desarchivado`);
      await fetchClientData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error unarchiving client:', error);
      setError('Error al desarchivar el cliente');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      // Check if client is assigned to any orders
      const isInUse = await checkClientInOrders(client.id);
      
      if (isInUse) {
        setError(`No se puede eliminar a ${getClientDisplayName()} porque tiene órdenes asignadas. Considere archivar el cliente en su lugar.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente a ${getClientDisplayName()}?\n\n` +
        `Esta acción no se puede deshacer. Si solo desea ocultar el cliente, ` +
        `considere archivarlo en su lugar.`
      )) {
        return;
      }

      await deleteDoc(doc(db, 'clients', client.id));
      setSuccess(`${getClientDisplayName()} ha sido eliminado permanentemente`);
      
      // Redirect to clients list after successful deletion
      setTimeout(() => {
        navigate('/clients');
      }, 2000);
      
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Error al eliminar el cliente');
      setTimeout(() => setError(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const fetchIncidents = async () => {
    try {
      const clientIncidents = await getClientIncidents(id);
      setIncidents(clientIncidents);
    } catch (error) {
      console.error('Error fetching client incidents:', error);
      setIncidents([]);
    }
  };

  const fetchClientData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch client details
      const clientDoc = await getDoc(doc(db, 'clients', id));
      if (!clientDoc.exists()) {
        setError('Cliente no encontrado');
        setLoading(false);
        return;
      }

      const clientData = { id: clientDoc.id, ...clientDoc.data() };
      
      // Update client blacklist status
      try {
        const blacklistStatus = await checkClientBlacklistStatus(id);
        clientData.isBlacklisted = blacklistStatus.isBlacklisted;
        clientData.activeIncidentsCount = blacklistStatus.activeIncidentsCount;
        clientData.mostRecentIncidentMotivo = blacklistStatus.mostRecentIncidentMotivo;
        clientData.mostRecentIncident = blacklistStatus.mostRecentIncident;
      } catch (error) {
        console.error('Error checking blacklist status:', error);
      }
      
      setClient(clientData);

      // Fetch orders for this client
      try {
        const ordersQuery = query(
          collection(db, 'orders'),
          where('cliente', '==', id)
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        
        // Process orders and populate equipment catalog data
        const ordersPromises = ordersSnapshot.docs.map(async (orderDoc) => {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          
          // Populate equipment catalog data if equipos exist
          if (orderData.equipos && orderData.equipos.length > 0) {
            const equipmentPromises = orderData.equipos.map(async (equipo) => {
              // If it has catalogId, fetch catalog equipment data
              if (equipo.catalogId) {
                try {
                  // Use equipmentService to get properly populated equipment data
                  const { getAllEquipment } = await import('../lib/equipmentService');
                  const allEquipment = await getAllEquipment(true);
                  const equipData = allEquipment.find(eq => eq.id === equipo.catalogId);
                  
                  if (equipData) {
                    return {
                      ...equipo,
                      catalogData: equipData,
                      type: 'catalog'
                    };
                  }
                  
                  // Fallback to direct Firestore access
                  const equipDoc = await getDoc(doc(db, 'equipment_catalog', equipo.catalogId));
                  if (equipDoc.exists()) {
                    return {
                      ...equipo,
                      catalogData: equipDoc.data(),
                      type: 'catalog'
                    };
                  }
                } catch (error) {
                  console.error('Error fetching catalog equipment:', error);
                }
              }
              
              // Return as exempt equipment if no catalog data
              return {
                ...equipo,
                type: 'exempt'
              };
            });
            
            const equipmentResults = await Promise.all(equipmentPromises);
            orderData.equipos = equipmentResults;
          }
          
          return orderData;
        });
        
        const ordersList = await Promise.all(ordersPromises);
        
        // Sort by folio (descending - highest to lowest)
        const sortedOrders = ordersList.sort((a, b) => {
          const folioA = parseInt(a.folio) || 0;
          const folioB = parseInt(b.folio) || 0;
          return folioB - folioA; // Descending order
        });
        
        setOrders(sortedOrders);
      } catch (error) {
        console.error('Error fetching client orders:', error);
        setOrders([]);
      }
      
      // Check if client is used in orders
      const inUse = await checkClientInOrders(id);
      setIsClientInUse(inUse);
      
    } catch (error) {
      console.error('Error fetching client data:', error);
      setError('Error al cargar la información del cliente');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const brandsQuery = query(collection(db, 'marcas'));
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      console.log('Brands collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  // Helper function to get brand object by ID or name
  const getBrandByNameOrId = (identifier) => {
    if (!identifier || brands.length === 0) {
      return { nombre: identifier || 'Sin marca', color: '#6B7280' };
    }
    
    // Try to find by ID first (new format)
    let brand = brands.find(b => b.id === identifier);
    
    // If not found, try by name (backward compatibility)
    if (!brand) {
      brand = brands.find(b => b.nombre === identifier);
    }
    
    // Return found brand or fallback
    return brand || { nombre: identifier, color: '#6B7280' };
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const getPrincipalContact = (contactos) => {
    if (!contactos || contactos.length === 0) return null;
    return contactos.find(c => c.esPrincipal) || contactos[0];
  };

  const getSortedContacts = (contactos) => {
    if (!contactos || contactos.length === 0) return [];
    // Sort contacts so primary contact appears first
    return [...contactos].sort((a, b) => {
      if (a.esPrincipal && !b.esPrincipal) return -1;
      if (!a.esPrincipal && b.esPrincipal) return 1;
      return 0;
    });
  };

  const getClientDisplayName = () => {
    const principal = getPrincipalContact(client.contactos);
    return client.nombreEmpresa || (principal ? principal.nombre : 'Cliente');
  };

  const getTipoClienteText = (tipo) => {
    switch (tipo) {
      case 'particular':
        return 'Particular';
      case 'empresa':
        return 'Empresa';
      default:
        return tipo || 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando información del cliente...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <Link 
              to={fromOrder && orderId ? `/orders/${orderId}` : "/clients"}
              className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>{fromOrder ? "Volver a la Orden" : "Volver a Clientes"}</span>
            </Link>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  // Pagination calculations
  const totalItems = orders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = orders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const principal = getPrincipalContact(client.contactos);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to={fromOrder && orderId ? `/orders/${orderId}` : "/clients"}
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{fromOrder ? "Volver a la Orden" : "Volver a Clientes"}</span>
          </Link>
        </div>

        {/* Success and Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}
        
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {/* Client Information Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-8 w-8 text-green-600 dark:text-green-400" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {client.nombreEmpresa || (principal ? principal.nombre : 'Cliente')}
                  </h1>
                  {client.nombreEmpresa && principal && (
                    <p className="text-lg text-gray-600 dark:text-gray-300 mt-1">{principal.nombre}</p>
                  )}
                  <div className="flex items-center space-x-2 mt-1">
                    {client.isArchived && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                        <Archive className="h-3 w-3 mr-1" />
                        Archivado
                      </span>
                    )}
                    <BlacklistBadge client={client} />
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              {canEdit() && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleEdit}
                    disabled={actionLoading}
                    className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50"
                    title="Editar cliente"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  
                  {client.isArchived ? (
                    <button
                      onClick={handleUnarchive}
                      disabled={actionLoading}
                      className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded disabled:opacity-50"
                      title="Desarchivar cliente"
                    >
                      <ArchiveRestore className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      onClick={handleArchive}
                      disabled={actionLoading}
                      className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded disabled:opacity-50"
                      title="Archivar cliente"
                    >
                      <Archive className="h-5 w-5" />
                    </button>
                  )}
                  
                  
                  {canDelete() && (
                    <button
                      onClick={!isClientInUse ? handleDelete : undefined}
                      disabled={actionLoading || isClientInUse}
                      className={`p-2 rounded disabled:opacity-50 ${
                        isClientInUse
                          ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                      title={
                        isClientInUse
                          ? 'No se puede eliminar: cliente usado en órdenes de trabajo'
                          : 'Eliminar cliente'
                      }
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Blacklist Warning Card */}
            <BlacklistWarningCard 
              client={client} 
              className="mb-6"
              clickable={true}
              onClick={handleBlacklistWarningClick}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Client Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Información del Cliente</h3>
                
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Tipo de Cliente</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{getTipoClienteText(client.tipoCliente)}</p>
                  </div>
                </div>

                {client.nombreEmpresa && (
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Nombre de Empresa</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{client.nombreEmpresa}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Fecha de Registro</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{formatDate(client.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Contacts Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Contactos</h3>
                
                {getSortedContacts(client.contactos).length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No hay contactos registrados
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getSortedContacts(client.contactos).map((contact, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                        {/* Contact Name with Primary Star */}
                        <div className="flex items-center space-x-2">
                          {contact.esPrincipal && (
                            <Star className="h-4 w-4 text-yellow-500 dark:text-yellow-400 fill-current" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {contact.nombre || 'Sin nombre'}
                              {contact.esPrincipal && (
                                <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                                  (Principal)
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        {/* Contact Details */}
                        <div className="space-y-2 ml-6">
                          {/* Phone */}
                          <div className="flex items-center space-x-3">
                            <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            <div>
                              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Teléfono</p>
                              <p className="text-sm text-gray-900 dark:text-white">{contact.telefono || 'N/A'}</p>
                            </div>
                          </div>

                          {/* Email */}
                          {contact.email && (
                            <div className="flex items-center space-x-3">
                              <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              <div>
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Email</p>
                                <p className="text-sm text-gray-900 dark:text-white">{contact.email}</p>
                              </div>
                            </div>
                          )}

                          {/* Note */}
                          {contact.nota && (
                            <div className="flex items-start space-x-3">
                              <Info className="h-4 w-4 text-gray-400 dark:text-gray-500 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Nota</p>
                                <p className="text-sm text-gray-900 dark:text-white">{contact.nota}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Estado</h3>
                
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Estado de Archivo</p>
                    <p className={`text-sm ${client.isArchived ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {client.isArchived ? 'Archivado' : 'Activo'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order History Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <ClipboardList className="h-6 w-6 text-green-600 dark:text-green-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Historial de Órdenes</h2>
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {orders.length} órdenes
              </span>
            </div>
          </div>

          <div className="p-6">
            {orders.length > 0 ? (
              <>
                {/* Mobile Card Layout */}
                <div className="block md:hidden space-y-4">
                  {currentOrders.map((order) => (
                    <div 
                      key={order.id} 
                      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white text-lg">{order.folio}</div>
                          {order.isArchived && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300 mt-1">
                              Archivada
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                            {getOrderTypeDisplayText(order.tipo_de_orden)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status_orden)}`}>
                            {order.status_orden || 'Sin estado'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-300">Fecha:</span>
                          <div className="text-right">
                            <div className="text-gray-900 dark:text-white">{formatDate(order.fecha)}</div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs">{getRelativeTime(order.fecha)}</div>
                          </div>
                        </div>
                        
                        {order.equipos && order.equipos.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Equipos:</span>
                            <div className="text-right flex-1 ml-2">
                              <div className="text-gray-900 dark:text-white text-xs">
                                {getEquipmentSummary(order.equipos)}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {canEdit() && (
                        <div className="flex justify-end space-x-1 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <Link
                            to={`/orders/${order.id}/edit`}
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50"
                            title="Editar orden"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                          {order.isArchived ? (
                            <button
                              onClick={(e) => {e.stopPropagation(); /* handleUnarchive */}}
                              disabled={actionLoading}
                              className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded disabled:opacity-50"
                              title="Desarchivar orden"
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {e.stopPropagation(); /* handleArchive */}}
                              disabled={actionLoading}
                              className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded disabled:opacity-50"
                              title="Archivar orden"
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete() && (
                            <button
                              onClick={(e) => {e.stopPropagation(); /* handleDelete */}}
                              disabled={actionLoading}
                              className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                              title="Eliminar orden"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Tablet and Desktop Table Layout */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Folio
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                          Equipos
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-48">
                          Estado
                        </th>
                        {canEdit() && (
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Acciones
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentOrders.map((order) => (
                        <tr key={order.id} className={`cursor-pointer ${
                            order.isArchived 
                              ? 'bg-gray-100 dark:bg-gray-900 hover:bg-gray-200 dark:hover:bg-gray-800' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`} onClick={() => navigate(`/orders/${order.id}`)}>
                          <td className="px-3 py-4 text-sm">
                            <div className="font-semibold text-gray-900 dark:text-white">{order.folio}</div>
                            {order.isArchived && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300 mt-1">
                                Archivada
                              </span>
                            )}
                            {/* Equipment info for medium screens */}
                            <div className="lg:hidden mt-2 space-y-1">
                              <div className="text-xs text-gray-600 dark:text-gray-300">
                                {getEquipmentSummary(order.equipos)}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm">
                            <div className="text-gray-900 dark:text-white">{formatDate(order.fecha)}</div>
                            <div className="text-gray-500 dark:text-gray-400 text-xs">{getRelativeTime(order.fecha)}</div>
                          </td>
                          <td className="px-3 py-4 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOrderTypeColor(order.tipo_de_orden)}`}>
                              {getOrderTypeDisplayText(order.tipo_de_orden)}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-sm hidden lg:table-cell">
                            <div className="max-w-xs">
                              {getEquipmentSummary(order.equipos)}
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm w-48">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(order.status_orden)}`}>
                              {order.status_orden || 'Sin estado'}
                            </span>
                          </td>
                          {canEdit() && (
                            <td className="px-3 py-4 text-sm">
                              <div className="flex space-x-1">
                                <Link
                                  to={`/orders/${order.id}/edit`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50"
                                  title="Editar orden"
                                >
                                  <Edit className="h-4 w-4" />
                                </Link>
                                {order.isArchived ? (
                                  <button
                                    onClick={(e) => {e.stopPropagation(); /* handleUnarchive */}}
                                    disabled={actionLoading}
                                    className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded disabled:opacity-50"
                                    title="Desarchivar orden"
                                  >
                                    <ArchiveRestore className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {e.stopPropagation(); /* handleArchive */}}
                                    disabled={actionLoading}
                                    className="p-2 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded disabled:opacity-50"
                                    title="Archivar orden"
                                  >
                                    <Archive className="h-4 w-4" />
                                  </button>
                                )}
                                {canDelete() && (
                                  <button
                                    onClick={(e) => {e.stopPropagation(); /* handleDelete */}}
                                    disabled={actionLoading}
                                    className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                                    title="Eliminar orden"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                    {/* Mobile pagination */}
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>

                    {/* Desktop pagination */}
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                          <span className="font-medium">{Math.min(endIndex, totalItems)}</span> de{' '}
                          <span className="font-medium">{totalItems}</span> órdenes
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Anterior</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                            </svg>
                          </button>

                          {(() => {
                            const pageNumbers = [];
                            const showEllipsis = totalPages > 7;
                            
                            if (showEllipsis) {
                              if (currentPage <= 3) {
                                for (let i = 1; i <= Math.min(5, totalPages); i++) {
                                  pageNumbers.push(i);
                                }
                                if (totalPages > 5) {
                                  pageNumbers.push('...');
                                  pageNumbers.push(totalPages);
                                }
                              } else if (currentPage >= totalPages - 2) {
                                pageNumbers.push(1);
                                pageNumbers.push('...');
                                for (let i = Math.max(totalPages - 4, 1); i <= totalPages; i++) {
                                  if (i > 1) pageNumbers.push(i);
                                }
                              } else {
                                pageNumbers.push(1);
                                pageNumbers.push('...');
                                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                  pageNumbers.push(i);
                                }
                                pageNumbers.push('...');
                                pageNumbers.push(totalPages);
                              }
                            } else {
                              for (let i = 1; i <= totalPages; i++) {
                                pageNumbers.push(i);
                              }
                            }
                            
                            return pageNumbers.map((number, index) => (
                              number === '...' ? (
                                <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-offset-0">
                                  ...
                                </span>
                              ) : (
                                <button
                                  key={number}
                                  onClick={() => handlePageChange(number)}
                                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 ${
                                    currentPage === number
                                      ? 'z-10 bg-blue-600 dark:bg-blue-700 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-blue-400'
                                      : 'text-gray-900 dark:text-gray-300'
                                  }`}
                                >
                                  {number}
                                </button>
                              )
                            ));
                          })()}

                          <button
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 dark:text-gray-500 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Siguiente</span>
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <ClipboardList className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin órdenes registradas</p>
                <p>Este cliente no tiene órdenes de trabajo asociadas.</p>
              </div>
            )}
          </div>
        </div>

      </div>
      
      {/* Client Form Modal */}
      <ClientForm
        isOpen={showForm}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editingClient={client}
      />
      
      {/* Blacklist Incident Form Modal */}
      <BlacklistIncidentForm
        isOpen={showIncidentForm}
        onClose={handleIncidentFormClose}
        onSuccess={handleIncidentFormSuccess}
        clientId={client?.id}
        clientName={getClientDisplayName()}
        editingIncident={editingIncident}
      />
    </div>
  );
};

export default ClientDetail;